/**
 * ContextMenu — thin styling wrapper around @radix-ui/react-context-menu.
 *
 * Re-exports the Radix primitives with Tailwind classNames that match
 * the rest of the app's surface (matches the existing DropdownMenu
 * pattern for visual consistency).
 *
 * Usage:
 *   <ContextMenu>
 *     <ContextMenu.Trigger asChild><div /></ContextMenu.Trigger>
 *     <ContextMenu.Content>
 *       <ContextMenu.Item onSelect={...}>Rename</ContextMenu.Item>
 *       <ContextMenu.Separator />
 *       <ContextMenu.Item danger onSelect={...}>Delete</ContextMenu.Item>
 *     </ContextMenu.Content>
 *   </ContextMenu>
 *
 * Touch devices: Radix automatically triggers via long-press, so the
 * menu works on mobile + tablet alongside desktop right-click.
 *
 * Data Room v2 Phase 6 (2026-05-26).
 */
import { forwardRef, type ReactNode } from 'react';
import * as Primitive from '@radix-ui/react-context-menu';
import { cn } from '@/lib/utils';

function Root({ children }: { children: ReactNode }) {
  return <Primitive.Root>{children}</Primitive.Root>;
}

const Trigger = Primitive.Trigger;
const Portal = Primitive.Portal;

const Content = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Primitive.Content>
>(({ className, ...props }, ref) => (
  <Primitive.Portal>
    <Primitive.Content
      ref={ref}
      className={cn(
        'z-50 min-w-[200px] overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
        className,
      )}
      {...props}
    />
  </Primitive.Portal>
));
Content.displayName = 'ContextMenu.Content';

interface ItemProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Primitive.Item>, 'asChild'> {
  /** Render in red — for destructive actions like Delete. */
  danger?: boolean;
}

const Item = forwardRef<HTMLDivElement, ItemProps>(
  ({ className, danger, ...props }, ref) => (
    <Primitive.Item
      ref={ref}
      className={cn(
        'relative flex select-none items-center gap-2 rounded px-2 py-1.5 text-sm outline-none cursor-pointer',
        'focus:bg-slate-100 data-[highlighted]:bg-slate-100',
        'data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed',
        danger
          ? 'text-red-600 focus:bg-red-50 data-[highlighted]:bg-red-50'
          : 'text-slate-700',
        className,
      )}
      {...props}
    />
  ),
);
Item.displayName = 'ContextMenu.Item';

const Separator = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Primitive.Separator>
>(({ className, ...props }, ref) => (
  <Primitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-slate-100', className)}
    {...props}
  />
));
Separator.displayName = 'ContextMenu.Separator';

const Label = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Primitive.Label>
>(({ className, ...props }, ref) => (
  <Primitive.Label
    ref={ref}
    className={cn(
      'px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold',
      className,
    )}
    {...props}
  />
));
Label.displayName = 'ContextMenu.Label';

export const ContextMenu = Object.assign(Root, {
  Trigger,
  Portal,
  Content,
  Item,
  Separator,
  Label,
});

export default ContextMenu;
